import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { compose } from "redux";
import {
  ActionList,
  BreadcrumbBar,
  IBreadcrumbBarItem,
  IIconTextLinkProps,
  ToTop,
  Head,
  myAccountLink,
} from "component-library";
import { withTranslation, WithTranslation } from "i18n";
import {
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
  FEATURE_FLAGS,
} from "shared-services";
import { IBreakdown } from "shared/models/breakdown";
import { FacetCategory } from "shared/models/facet";
import { IUser } from "shared/models/user";
import { navigator } from "shared/services/navigator";

import {
  PageTitleKey,
  SCROLLING_CONTAINER_ID,
  USE_BROWSER_BACK_KEY,
} from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getCountries } from "store/location/location.actions";
import { GetCountries } from "store/location/location.types";
import { getBreakdownPage } from "store/page/page.actions";
import { GetBreakdownPage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";
import {
  GetPageTranslation,
  ITranslationState,
} from "store/translation/translation.types";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, ShowPopup } from "store/ui/ui.types";

import { IActionList, IActionListItem } from "models/common";
import { JobItemType } from "models/job";

import { IPrepareBreakdownViewData } from "helpers/breakdownViewHelpers";
import copyPrefixedObjectKeys from "helpers/copyPrefixedObjectKeys";
import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";
import applicationMap from "services/applicationMap";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";

import BreakdownView, {
  IBreakdownViewRole,
  IBreakdownViewTexts,
} from "components/BreakdownView/BreakdownView";

import {
  ActionListItemKey,
  prepareActionList,
  prepareBreadcrumbs,
  prepareBreakdownViewRoles,
  prepareBreakdownViewTexts,
} from "./BreakdownPage.helpers";

import "./BreakdownPage.scss";

export interface IBreakdownPageActions {
  getPageTranslation: GetPageTranslation;
  getBreakdownPage: GetBreakdownPage;
  getCountries: GetCountries;
  getFacets: GetFacets;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IBreakdownPageMatchParams {
  breakdownId: string;
}

export interface IBreakdownPageStoreProps {
  translation: ITranslationState;
  breakdown: IBreakdown;
  breadcrumbs: IBreadcrumbBarItem[];
  breakdownViewTexts: IBreakdownViewTexts;
  breakdownRoles: IBreakdownViewRole[];
  actionList: IActionList;
  pageTitle: string;
  user: IUser;
  markAsRead: SetJobActionType;
}

export interface IBreakdownPageComposedProps
  extends IBreakdownPageActions,
    IBreakdownPageStoreProps,
    IWithPageDataProps,
    RouteComponentProps<IBreakdownPageMatchParams> {}

export interface IBreakdownPageProps
  extends IBreakdownPageComposedProps,
    WithTranslation,
    LaunchDarklyClientWithFlags {}

interface IBreakdownPageState {
  returnUri?: string;
  [USE_BROWSER_BACK_KEY]?: boolean;
}

interface IRouteState {
  [USE_BROWSER_BACK_KEY]: boolean;
}

export class BreakdownPage extends React.Component<
  IBreakdownPageProps,
  IBreakdownPageState
> {
  private readonly actionListHandlers: {
    [ActionListItemKey.BackToJobsFeed](): void;
  };

  readonly state: Readonly<IBreakdownPageState> = {};

  constructor(props: IBreakdownPageProps) {
    super(props);

    const actionListHandlers = {
      [ActionListItemKey.BackToJobsFeed]: this.handleBackClick,
    };
    this.actionListHandlers = copyPrefixedObjectKeys(
      actionListHandlers,
      "more-dropdown_"
    );
  }

  public componentDidMount() {
    void this.markAsRead();
    this.setReturnUri();
  }

  public render() {
    const {
      pageTitle,
      breadcrumbs,
      breakdown,
      breakdownRoles,
      breakdownViewTexts,
      user,
      actionList,
      t,
    } = this.props;

    return (
      <section className="c-breakdown-page">
        <Head registerPageView={this.registerPageView}>
          <title>{pageTitle}</title>
        </Head>
        <BreadcrumbBar items={breadcrumbs} />
        <BreakdownView
          breakdown={breakdown}
          roles={breakdownRoles}
          texts={breakdownViewTexts}
          limitWidth={true}
          user={user}
          actionList={
            <div>
              <ActionList
                items={this.prepareActionListItems()}
                moreButtonText={actionList.moreButtonText}
                variant="pageHeader"
              />
            </div>
          }
        />
        <ToTop
          label={t("common:label.to.top")}
          scrollableContainerId={SCROLLING_CONTAINER_ID}
        />
      </section>
    );
  }

  private createHandlerActionListItemClick =
    (id: string = "") =>
    (e: React.MouseEvent) => {
      const handler = this.actionListHandlers[id];

      if (handler) {
        e.preventDefault();
        handler();
      }
    };

  private handleBackClick = () => {
    const { returnUri, [USE_BROWSER_BACK_KEY]: useBrowserBack } = this.state;

    if (useBrowserBack) {
      navigator.goBack();
    } else {
      navigator.goTo(returnUri || applicationMap.links.jobsMyFilters());
    }
  };

  private prepareActionListItems(): IIconTextLinkProps[] {
    return this.props.actionList.items?.map(
      (item: IActionListItem): IIconTextLinkProps => ({
        ...item,
        handleClick: this.createHandlerActionListItemClick(item.id),
      })
    );
  }

  private async markAsRead() {
    const id = jobService.generateInteractionItemId(
      JobItemType.Linkmail,
      this.props.breakdown.id
    );
    try {
      await this.props.markAsRead([{ id, jobItemType: JobItemType.Linkmail }]);
    } catch (e) {
      console.error(e);
    }
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.breakdown(this.props.pageTitle);
  };

  private setReturnUri() {
    navigator.handleReturnUri({
      returnUriCallback: (returnUri: string) => {
        this.setState({
          returnUri: decodeURIComponent(returnUri),
          [USE_BROWSER_BACK_KEY]:
            navigator.getCurrentRoute<IRouteState>().state?.[
              USE_BROWSER_BACK_KEY
            ],
        });
        navigator.clearSearch({ replace: true });
      },
    });
  }
}

async function loadExternalData(props: IBreakdownPageProps) {
  return Promise.all([
    props.getCountries(props.locale),
    props.getFacets(
      [
        FacetCategory.ContractType,
        FacetCategory.ProductionType,
        FacetCategory.Gender,
        FacetCategory.Appearance,
        FacetCategory.Disability,
        FacetCategory.ComplaintCategory,
        FacetCategory.EthnicBackground,
        FacetCategory.HeritageAndEthnicity,
      ],
      props.locale
    ),
  ]);
}

const pageDataLoaders: ILoaders<IBreakdownPageProps> = {
  loadTranslation(props, isLocaleChanged) {
    return props.getPageTranslation(
      { pageKey: PageKey.Breakdown, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props) {
    await loadExternalData(props);

    return props.getBreakdownPage(
      props.match.params.breakdownId.toLowerCase(),
      props.cancellationToken
    );
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  props: IBreakdownPageProps
): IBreakdownPageStoreProps => {
  const { t, flags } = props;
  const isYPPlayingAgeEnabled = flags[FEATURE_FLAGS.YPsPlayingAgeMin];
  const pageTranslation = state.translation.pages[PageKey.Breakdown]!;
  const commonTranslation = state.translation.common;
  const breakdown = state.page.data!.breakdown!;
  const prepareBreakdownViewData: IPrepareBreakdownViewData = {
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
    genders: state.facet[FacetStoreKey.Gender]!,
    disabilities: state.facet[FacetStoreKey.Disability]!,
    appearances: state.facet[FacetStoreKey.Appearance]!,
    contractTypes: state.facet[FacetStoreKey.ContractType]!,
    countries: state.location.countries!,
  };
  const breakdownViewTexts = prepareBreakdownViewTexts(
    pageTranslation,
    state.translation.common,
    breakdown,
    prepareBreakdownViewData
  );

  return {
    translation: state.translation,
    breakdown,
    breadcrumbs: prepareBreadcrumbs(pageTranslation, myAccountLink),
    breakdownViewTexts,
    breakdownRoles: prepareBreakdownViewRoles({
      pageTranslation,
      breakdown,
      prepareBreakdownViewData,
      t,
      isYPPlayingAgeEnabled,
    }),
    actionList: prepareActionList(pageTranslation, breakdown.id),
    pageTitle: getPageTitle(
      commonTranslation,
      PageTitleKey.Breakdown,
      (format) => format.replace("{breakdownName}", breakdown.production.title)
    ),
    user: state.user.data!,
    markAsRead: (...args) => jobService.markAsRead(...args),
  };
};

const mapDispatchToProps = {
  setLoading,
  getPageTranslation,
  getBreakdownPage,
  getCountries,
  getFacets,
  showPopup,
  hidePopup,
};

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<IBreakdownPageProps>(PageKey.Breakdown, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(BreakdownPage) as React.ComponentType<IBreakdownPageComposedProps>;
